import * as WebBrowser from 'expo-web-browser';
import { useRouter } from 'expo-router';
import {
  ChevronLeft,
  ExternalLink,
  Eye,
  EyeOff,
  Key,
  ShieldCheck,
  Trash2,
} from 'lucide-react-native';
import { useEffect, useState } from 'react';
import {
  Linking,
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  View,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';

import { useConfirm } from '@/components/ConfirmDialog';
import { Fonts, Palette, Radius, Spacing } from '@/constants/theme';
import { goBackOrTo } from '@/lib/nav';
import {
  API_KEY_SLOTS,
  type ApiKeySlot,
  getApiKey,
  maskKey,
  setApiKey,
} from '@/lib/api-keys';
import { webNoOutline } from '@/lib/web-style';

/**
 * Single-user admin screen for entering / rotating API keys without ever
 * putting them in committed code. Keys are stored in expo-secure-store on
 * native; localStorage on web. They never leave the device.
 */
export default function ApiKeysScreen() {
  const router = useRouter();
  const confirm = useConfirm();
  const [values, setValues] = useState<Record<string, string>>({});
  const [stored, setStored] = useState<Record<string, string | null>>({});
  const [reveal, setReveal] = useState<Record<string, boolean>>({});
  const [status, setStatus] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      const next: Record<string, string | null> = {};
      for (const slot of API_KEY_SLOTS) {
        next[slot.key] = await getApiKey(slot.key);
      }
      setStored(next);
    })();
  }, []);

  const onSave = async (slot: ApiKeySlot) => {
    const v = (values[slot.key] ?? '').trim();
    await setApiKey(slot.key, v);
    setStored((prev) => ({ ...prev, [slot.key]: v || null }));
    setValues((prev) => ({ ...prev, [slot.key]: '' }));
    setStatus(v ? `${slot.label} saved.` : `${slot.label} cleared.`);
    setTimeout(() => setStatus(null), 3000);
  };

  const onClear = async (slot: ApiKeySlot) => {
    const ok = await confirm({
      title: 'Remove key?',
      message: `Remove the saved ${slot.label} key from this device?`,
      destructive: true,
      confirmLabel: 'Remove',
    });
    if (!ok) return;
    await setApiKey(slot.key, '');
    setStored((prev) => ({ ...prev, [slot.key]: null }));
    setStatus(`${slot.label} removed.`);
    setTimeout(() => setStatus(null), 3000);
  };

  const openSignup = async (url: string) => {
    try {
      if (Platform.OS === 'web') {
        // eslint-disable-next-line no-undef
        window.open(url, '_blank', 'noopener,noreferrer');
      } else {
        await WebBrowser.openBrowserAsync(url);
      }
    } catch {
      try {
        await Linking.openURL(url);
      } catch {
        // give up silently
      }
    }
  };

  return (
    <SafeAreaView style={styles.container} edges={['top', 'bottom']}>
      <View style={styles.topbar}>
        <Pressable onPress={() => goBackOrTo(router, '/(tabs)/settings')} style={({ pressed }) => [styles.back, pressed && { opacity: 0.7 }]} hitSlop={12}>
          <ChevronLeft size={22} color={Palette.ink} strokeWidth={2} />
        </Pressable>
        <Text style={styles.topbarTitle}>API keys</Text>
        <View style={{ width: 36 }} />
      </View>

      <ScrollView contentContainerStyle={{ paddingBottom: 80 }}>
        <Text style={styles.h1}>API keys</Text>
        <Text style={styles.sub}>
          Paste keys here to unlock OCR, address autocomplete, and weather.
          Keys stay on this device — they&apos;re never sent to Homestead
          servers and never exported.
        </Text>

        <View style={styles.callout}>
          <ShieldCheck size={16} color={Palette.forest} strokeWidth={1.8} />
          <Text style={styles.calloutText}>
            <Text style={styles.calloutBold}>Always restrict your keys</Text> in
            Cloud Console: HTTP referrer + app bundle ID + per-API restrictions.
            Set a billing budget alert before saving any production key.
          </Text>
        </View>

        {API_KEY_SLOTS.map((slot) => {
          const has = !!stored[slot.key];
          const revealed = reveal[slot.key] ?? false;
          return (
            <View key={slot.key} style={styles.card}>
              <View style={styles.cardHead}>
                <View style={styles.cardHeadLeft}>
                  <View style={styles.keyIcon}>
                    <Key size={16} color={Palette.forest} strokeWidth={1.8} />
                  </View>
                  <View style={{ flex: 1, minWidth: 0 }}>
                    <Text style={styles.cardTitle}>{slot.label}</Text>
                    <Text style={styles.cardDesc}>{slot.description}</Text>
                  </View>
                </View>
              </View>

              {has ? (
                <View style={styles.savedRow}>
                  <View style={styles.savedDot} />
                  <Text style={styles.savedText}>
                    Saved · {revealed ? stored[slot.key] : maskKey(stored[slot.key])}
                  </Text>
                  <Pressable
                    onPress={() =>
                      setReveal((p) => ({ ...p, [slot.key]: !p[slot.key] }))
                    }
                    hitSlop={6}>
                    {revealed ? (
                      <EyeOff size={14} color={Palette.inkSoft} strokeWidth={1.8} />
                    ) : (
                      <Eye size={14} color={Palette.inkSoft} strokeWidth={1.8} />
                    )}
                  </Pressable>
                  <Pressable onPress={() => onClear(slot)} hitSlop={6} style={styles.removeIcon}>
                    <Trash2 size={14} color={Palette.clay} strokeWidth={1.8} />
                  </Pressable>
                </View>
              ) : null}

              <TextInput
                value={values[slot.key] ?? ''}
                onChangeText={(v) => setValues((p) => ({ ...p, [slot.key]: v }))}
                placeholder={has ? 'Paste a replacement key' : slot.placeholder}
                placeholderTextColor={Palette.inkFaint}
                secureTextEntry={!(reveal[slot.key] ?? false)}
                autoCorrect={false}
                autoCapitalize="none"
                style={[styles.input, webNoOutline]}
              />

              <View style={styles.actions}>
                <Pressable
                  onPress={() => openSignup(slot.signupUrl)}
                  style={({ pressed }) => [styles.linkBtn, pressed && { opacity: 0.85 }]}>
                  <ExternalLink size={13} color={Palette.forest} strokeWidth={1.8} />
                  <Text style={styles.linkBtnText}>Get a key</Text>
                </Pressable>
                <Pressable
                  onPress={() => onSave(slot)}
                  disabled={!(values[slot.key] ?? '').trim() && !has}
                  style={({ pressed }) => [
                    styles.saveBtn,
                    !(values[slot.key] ?? '').trim() && { opacity: 0.5 },
                    pressed && { transform: [{ scale: 0.97 }] },
                  ]}>
                  <Text style={styles.saveBtnText}>Save</Text>
                </Pressable>
              </View>
            </View>
          );
        })}

        {status ? (
          <View style={styles.statusBar}>
            <Text style={styles.statusText}>{status}</Text>
          </View>
        ) : null}
      </ScrollView>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: Palette.sand },
  topbar: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between',
    paddingHorizontal: Spacing.four, paddingTop: Spacing.two, paddingBottom: Spacing.two,
  },
  back: {
    width: 36, height: 36, borderRadius: 18, borderWidth: 1,
    borderColor: Palette.line, backgroundColor: Palette.card,
    alignItems: 'center', justifyContent: 'center',
  },
  topbarTitle: { fontFamily: Fonts.sansBold, fontSize: 14, color: Palette.ink },
  h1: {
    marginHorizontal: Spacing.four, marginTop: Spacing.three,
    fontFamily: Fonts.serif, fontSize: 24, color: Palette.ink, letterSpacing: -0.3,
  },
  sub: {
    marginHorizontal: Spacing.four, marginTop: 4,
    fontSize: 13, lineHeight: 19, color: Palette.inkSoft, fontFamily: Fonts.sansMedium,
  },
  callout: {
    marginTop: Spacing.three,
    marginHorizontal: Spacing.four,
    padding: 12,
    borderRadius: 12,
    backgroundColor: '#EEF3EE',
    borderWidth: 1, borderColor: '#C4D7C8',
    flexDirection: 'row',
    gap: 8,
  },
  calloutText: {
    flex: 1,
    fontFamily: Fonts.sansMedium,
    fontSize: 13,
    color: Palette.forest,
    lineHeight: 17,
  },
  calloutBold: { fontFamily: Fonts.sansBold },
  card: {
    marginTop: Spacing.four,
    marginHorizontal: Spacing.four,
    backgroundColor: Palette.card,
    borderWidth: 1, borderColor: Palette.line,
    borderRadius: Radius.xl,
    padding: Spacing.four,
  },
  cardHead: { flexDirection: 'row', alignItems: 'flex-start' },
  cardHeadLeft: { flexDirection: 'row', gap: 10, alignItems: 'flex-start', flex: 1 },
  keyIcon: {
    width: 32, height: 32, borderRadius: 10,
    backgroundColor: '#EAF0EA',
    alignItems: 'center', justifyContent: 'center',
  },
  cardTitle: { fontFamily: Fonts.sansBold, fontSize: 14, color: Palette.ink },
  cardDesc: {
    fontFamily: Fonts.sansMedium, fontSize: 13, color: Palette.inkSoft,
    marginTop: 2, lineHeight: 17,
  },
  savedRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginTop: 12,
    padding: 10,
    backgroundColor: '#EEF3EE',
    borderRadius: 10,
  },
  savedDot: {
    width: 7, height: 7, borderRadius: 4,
    backgroundColor: Palette.forest,
  },
  savedText: {
    flex: 1,
    fontFamily: Fonts.sansBold,
    fontSize: 12.5,
    color: Palette.forest,
    letterSpacing: 0.3,
  },
  removeIcon: { padding: 2 },
  input: {
    marginTop: 10,
    borderWidth: 1.5,
    borderColor: Palette.line,
    backgroundColor: Palette.sand,
    borderRadius: 11,
    paddingHorizontal: 12,
    paddingVertical: 11,
    fontFamily: Fonts.sansSemibold,
    fontSize: 13,
    color: Palette.ink,
  },
  actions: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginTop: 10,
  },
  linkBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 5,
    paddingHorizontal: 11,
    paddingVertical: 8,
    borderRadius: 999,
    borderWidth: 1, borderColor: Palette.line,
    backgroundColor: Palette.sand,
  },
  linkBtnText: { fontFamily: Fonts.sansBold, fontSize: 13, color: Palette.forest },
  saveBtn: {
    backgroundColor: Palette.forest,
    paddingHorizontal: 18,
    paddingVertical: 10,
    borderRadius: 12,
  },
  saveBtnText: { fontFamily: Fonts.sansBold, fontSize: 13, color: Palette.cream },
  statusBar: {
    marginTop: Spacing.four,
    marginHorizontal: Spacing.four,
    padding: 12,
    borderRadius: 12,
    backgroundColor: Palette.forest,
  },
  statusText: { color: Palette.cream, fontFamily: Fonts.sansBold, fontSize: 13 },
});
